import * as React from "react";
import { useRef } from "react";
import S from "./Header.styled";
import useHeaderData from "./useHeaderData";
import { Popover, Transition } from "@headlessui/react";
import { Link } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import QuoteModal from "../../QuoteModal";
import MobileMenu from "../../MobileMenu";
import TopMenu from "../TopMenu";
import { Bars3Icon } from "@heroicons/react/24/outline";

import { useLocalization } from "../../../contexts/Localization";
import { IWPLink } from "@/src/types";

type LinkType = {
  link: IWPLink;
  title: string;
  hasSubmenu: boolean;
  sublinks: {
    title: string;
    target: string;
    link: IWPLink;
    description: string;
    boxicon: string;
  }[];
  submenuLink: IWPLink;
  submenuTitle: string;
};

const Header: React.FC = () => {
  const { language, translations } = useLocalization();
  const wpData = useHeaderData(language);
  const data = wpData.header;

  const ref = useRef(null);

  var currentLang = language;
  if (currentLang === "sv") {
    currentLang = "";
  }

  React.useEffect(() => {
    window.onscroll = function () {
      Sticky();
    };

    var header = document.getElementById("metlabHeader");
    var topHeader = document.getElementById("headerTopMenu");
    var sticky = 0;

    function Sticky() {
      if (header == null) return;

      if (window.pageYOffset > sticky) {
        header.classList.add("sticky");
        topHeader?.classList.add("sticky");
      } else {
        header.classList.remove("sticky");
        topHeader?.classList.add("sticky");
      }
    }

    Sticky();
  }, []);

  function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
  }

  //  Logo image data
  const logoImageData = data.logo?.localFile?.childImageSharp?.gatsbyImageData
    ? getImage(data.logo.localFile.childImageSharp.gatsbyImageData)
    : null;

  return (
    <Popover>
      {({ open }) => (
        <>
          <TopMenu
            phoneNumber={data.topHeader.phoneNumber}
            email={data.topHeader.email}
            link={data.topHeader.link.url}
            linkText={data.topHeader.link.title}
          />

          <div
            id="metlabHeader"
            className={
              "relative py-3 sm:px-6 bg-white w-full shadow-lg transition duration-200 ease-in-out mainNav"
            }
          >
            <nav
              className="max-w-7xl py-6 sm:px-6 w-full mx-auto relative flex items-center justify-between sm:h-10 mainNav"
              aria-label="Global"
            >
              <S.metlabMenu
                className={
                  "flex items-center flex-grow flex-shrink-0 lg:flex-grow-0"
                }
              >
                <div className="flex items-center justify-between w-full md:w-auto">
                  <Link to={"/" + currentLang}>
                    <span className="sr-only">Metlab</span>
                    {logoImageData && (
                      <S.Logo
                        image={logoImageData}
                        alt={"Metlab logga"}
                        objectFit={"contain"}
                        className={"h-8 w-auto sm:h-12"}
                      />
                    )}
                  </Link>
                  <div className="-mr-2 flex items-center md:hidden">
                    <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-metlab-500">
                      <span className="sr-only">Open Menu Text</span>
                      <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="hidden md:block md:ml-10 md:pr-4 md:space-x-8">
                  <Popover.Group as="nav" className="hidden md:flex space-x-6">
                    {data.links.map((link: LinkType, i: number) => (
                      <Popover className="" key={i}>
                        {({ open }) => (
                          <>
                            <Link to={""}>
                              <Popover.Button
                                className={classNames(
                                  open ? "text-metlab-500" : "text-mGray-700",
                                  "group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-metlab-500 focus:outline-none"
                                )}
                              >
                                {link.hasSubmenu && link?.sublinks?.length ? (
                                  link.link?.title
                                ) : (
                                  <Link to={link.link?.url}>
                                    {link.link?.title}
                                  </Link>
                                )}
                              </Popover.Button>
                            </Link>

                            {link.hasSubmenu && link?.sublinks?.length && (
                              <Transition
                                show={open}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1"
                              >
                                <Popover.Panel
                                  static
                                  className="absolute z-20 -ml-4 mt-3 transform px-2 sm:px-0 lg:ml-0"
                                >
                                  <S.metlabDropdown
                                    className={
                                      "rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                                    }
                                  >
                                    {link.submenuTitle && (
                                      <S.metlabDropdownMenuItem
                                        className={
                                          "relative grid bg-white px-4 py-6 pb-0"
                                        }
                                      >
                                        <div>
                                          <div className="ml-4">
                                            <h3 className="text-xl font-bold text-gray-900 m-0">
                                              {link.submenuTitle}
                                            </h3>
                                          </div>
                                        </div>
                                      </S.metlabDropdownMenuItem>
                                    )}
                                    <S.metlabDropdownMenuItem
                                      className={`${
                                        link.hasSubmenu
                                          ? "relative grid gap-6 lg:grid-cols-2 grid-cols-3 bg-white px-5 pt-4 py-6 sm:gap-8 sm:p-8 sm:pt-6 "
                                          : "relative grid gap-6 grid-cols-1 bg-white px-5 pt-4 py-6 sm:gap-8 sm:p-8 sm:pt-6 "
                                      }`}
                                    >
                                      {link.sublinks.map(
                                        (
                                          {
                                            title,
                                            link,
                                            target,
                                            description,
                                            boxicon,
                                          },
                                          i
                                        ) => (
                                          <a
                                            key={i}
                                            href={link?.url}
                                            className="-m-3 p-3 flex items-start rounded-lg hover:bg-gray-50"
                                          >
                                            <span
                                              className={
                                                boxicon +
                                                " h-10 w-10 text-2xl leading-none text-metlab-500 bg-metlab-200 p-2 rounded-full mb-3.5"
                                              }
                                            ></span>
                                            <Link to={link?.url}>
                                              <div className="ml-4">
                                                <p className="text-base font-medium text-gray-900">
                                                  {link?.title && link?.title}
                                                </p>
                                                <div
                                                  className="mt-1 text-sm text-gray-500"
                                                  dangerouslySetInnerHTML={{
                                                    __html:
                                                      description &&
                                                      description,
                                                  }}
                                                ></div>
                                              </div>
                                            </Link>
                                          </a>
                                        )
                                      )}

                                      {link.submenuLink && (
                                        <div
                                          className={"float-right align-right"}
                                        >
                                          <Link to={link.submenuLink.url}>
                                            {link.submenuLink.title + " >>"}
                                          </Link>
                                        </div>
                                      )}
                                    </S.metlabDropdownMenuItem>
                                  </S.metlabDropdown>
                                </Popover.Panel>
                              </Transition>
                            )}
                          </>
                        )}
                      </Popover>
                    ))}
                  </Popover.Group>
                </div>
              </S.metlabMenu>
              <QuoteModal
                ref={ref}
                title={data.quoteLabel}
                boxicon={wpData?.form?.boxicon && wpData?.form?.boxicon}
              ></QuoteModal>
              <div
                id="request-quote-btn"
                style={{ cursor: "pointer" }}
                onClick={() => ref.current.showModal()}
              >
                <div className="font-medium text-white border border-metlab-500 bg-metlab-500 px-4 py-2 text-base font-medium rounded-full items-center justify-between hidden md:flex">
                  <i className="bx bx-file-blank mr-2"></i> {data.quoteLabel}
                </div>
              </div>
            </nav>
          </div>

          <Transition
            show={open}
            enter="duration-150 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100 w-100 absolute w-full z-50 h-500"
            leave="duration-100 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Popover.Panel
              className="absolute z-50 top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden"
              focus
              static
            >
              <MobileMenu
                logoImageData={logoImageData}
                links={data.links}
                quoteRef={ref}
                quoteLabel={data.quoteLabel}
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
export default Header;
